import { useMemo } from 'react'
import moment from 'moment'
import { extractNoteImageUrls } from 'utils/noteImages'
import type {
	Contributor,
	DateBucket,
	NoteFacets,
	NoteFilterId,
	NoteImageEntry,
	WorkspaceNote,
} from 'components/NotesWorkspace/notesWorkspace.types'

/**
 * Derives everything the Notes rail displays from a plain note array.
 *
 * All of it is client-side by necessity: the note endpoints accept only an
 * entity id, sort is hardcoded `createdAt: -1`, and there is no paging, so every
 * surface already holds its whole note set in Redux. Faceting it here costs
 * nothing and needs no backend change.
 *
 * Pure apart from `useMemo`, so it can be tested without a store.
 */

const timeOf = (note: WorkspaceNote): number =>
	new Date(note.updatedAt || note.createdAt || 0).getTime()

/** Portfolio notes have no `parentid` at all, so match null and undefined alike. */
const isReply = (note: WorkspaceNote): boolean => note.parentid != null

const authorIdOf = (note: WorkspaceNote): string | undefined => {
	const author = note.userid
	if (!author) return undefined
	return typeof author === 'string' ? author : author._id
}

export const buildNoteFacets = (notes: WorkspaceNote[]): NoteFacets => {
	const topLevel: WorkspaceNote[] = []
	const repliesByParent: Record<string, WorkspaceNote[]> = {}

	for (const note of notes) {
		if (!note) continue
		if (isReply(note)) {
			const parent = String(note.parentid)
			;(repliesByParent[parent] ||= []).push(note)
		} else {
			topLevel.push(note)
		}
	}

	topLevel.sort((a, b) => timeOf(b) - timeOf(a))
	// Replies read oldest-first, the order a conversation happened in.
	for (const parent of Object.keys(repliesByParent)) {
		repliesByParent[parent].sort((a, b) => timeOf(a) - timeOf(b))
	}

	const contributorMap = new Map<string, Contributor>()
	for (const note of notes) {
		const id = authorIdOf(note)
		if (!id) continue
		const existing = contributorMap.get(id)
		if (existing) {
			existing.count += 1
			continue
		}
		contributorMap.set(id, {
			id,
			name: note.userid?.name || note.userid?.firstName || note.userid?.email || 'Unknown',
			email: note.userid?.email,
			image: note.userid?.image,
			count: 1,
		})
	}
	const contributors = [...contributorMap.values()].sort((a, b) => b.count - a.count)

	// Images in feed order, each remembering the note it lives in so a rail
	// click can scroll to that note and open it at the right index.
	const images: NoteImageEntry[] = []
	for (const note of topLevel) {
		extractNoteImageUrls(note.note || '').forEach((url, indexInNote) => {
			images.push({ url, noteId: note._id, indexInNote })
		})
	}

	const replyCount = Object.values(repliesByParent).reduce((sum, list) => sum + list.length, 0)

	return {
		topLevel,
		repliesByParent,
		contributors,
		images,
		counts: {
			notes: topLevel.length,
			replies: replyCount,
			images: images.length,
			contributors: contributors.length,
			// Notes carrying at least one image, which is what the "With images"
			// filter actually returns. The raw image count would overstate it.
			notesWithImages: new Set(images.map(entry => entry.noteId)).size,
		},
	}
}

export interface NoteFilterState {
	filter: NoteFilterId
	/** When set, only this author's notes show. */
	authorId?: string | null
}

/**
 * Applies the rail's quick filter and author selection to a note list.
 * Search is applied separately by `filterNotesBySearch` so the two compose.
 */
export const applyNoteFilters = (
	notes: WorkspaceNote[],
	{ filter, authorId }: NoteFilterState,
	context: { currentUserId: string; repliesByParent: Record<string, WorkspaceNote[]> },
): WorkspaceNote[] => {
	let result = notes

	if (authorId) {
		result = result.filter(note => authorIdOf(note) === authorId)
	}

	switch (filter) {
		case 'images':
			result = result.filter(note => extractNoteImageUrls(note.note || '').length > 0)
			break
		case 'mine':
			// With no signed-in id every note would match on `undefined`, which
			// would read as "all of these are mine". Show none instead.
			result = context.currentUserId
				? result.filter(note => authorIdOf(note) === context.currentUserId)
				: []
			break
		case 'replies':
			result = result.filter(note => (context.repliesByParent[note._id]?.length ?? 0) > 0)
			break
		case 'all':
		default:
			break
	}

	return result
}

/**
 * Groups notes into Today / This week / Earlier.
 *
 * The feed uses these as sticky dividers so a long list stays legible, and the
 * rail uses their counts to jump between them. Empty buckets are dropped.
 */
export const groupNotesByDate = (notes: WorkspaceNote[]): DateBucket[] => {
	const startOfToday = moment().startOf('day')
	const weekAgo = moment().subtract(7, 'days')

	const buckets: Record<string, WorkspaceNote[]> = { today: [], week: [], earlier: [] }

	for (const note of notes) {
		const when = moment(note.createdAt)
		if (when.isSameOrAfter(startOfToday)) buckets.today.push(note)
		else if (when.isAfter(weekAgo)) buckets.week.push(note)
		else buckets.earlier.push(note)
	}

	return (
		[
			{ id: 'today' as const, label: 'Today', notes: buckets.today },
			{ id: 'week' as const, label: 'Earlier this week', notes: buckets.week },
			{ id: 'earlier' as const, label: 'Earlier', notes: buckets.earlier },
		] satisfies DateBucket[]
	).filter(bucket => bucket.notes.length > 0)
}

const useNoteFacets = (notes: WorkspaceNote[]): NoteFacets =>
	useMemo(() => buildNoteFacets(notes), [notes])

export default useNoteFacets
